import { useDeferredValue, useState } from 'react';
import Image from 'next/image';
import styled from 'styled-components';

import classNames from 'classnames';
import { getComponentMargin, getAnchorId } from '@/utils/builderUtils';
import { moreDescriptiveAriaLabel } from '@/utils/utils';
import useMediaQuery from '@/hooks/useMediaQuery';

import beTwMerge from '@/utils/beTwMerge';
import { Action } from '@/components/SingleImageTile/SingleImageTile';
import Button from '@/components/homepage/Button/Button';
import DropdownButton from '@/components/homepage/DropdownButton';
import Title from '@/components/Title/Title';
import RichText from '../../RichText/RichText';

interface ICaratSliderHandGraphicProps {
  /**
   * Turn on to add margin to the bottom of the component
   */
  margin?: boolean;
  /**
   * Give to the component an anchor link id
   */
  anchorLinkID?: string;
  /**
   * To position the image either to the left or to the right
   */
  contentPosition: string;
  /**
   * To position the image either to the center or to the start
   */
  mediaAlignment: 'start' | 'center';
  /**
   *  Rich text content of the Block
   */
  content: string;
  /**
   * The list of cta buttons
   * Props.
   * - text*: Text of the button
   * - url*: url to link to
   * - newTab*: If the link should open in a new tab
   */
  actions?: Action[];
  /**
   * Optional large header
   */
  optionalLargeHeader?: {
    content: string;
    titleHTMLTag?: 'h1' | 'h2' | 'h3';
    titleSize?: 'extraLarge' | 'large' | 'medium' | 'small';
    titleWeight?: 'light' | 'normal' | 'medium' | 'bold' | 'black';
  };
}

const Container = styled.div`
  -webkit-text-size-adjust: 100%;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
  color: #333;
  font-size: 16px;
  line-height: 1.56;
  box-sizing: border-box;
  max-height: 999999px;
  min-height: 1px;
  width: 100%;

  .budget-box {
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
    color: #333;
    font-size: 16px;
    line-height: 1.56;
    box-sizing: border-box;
    max-height: 999999px;
  }

  .carat-weight-responsive {
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
    color: #333;
    font-size: 16px;
    line-height: 1.56;
    box-sizing: border-box;
    max-height: 999999px;
  }

  .search-noUiSlider-wrap {
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
    color: #333;
    font-size: 16px;
    line-height: 1.56;
    box-sizing: border-box;
    max-height: 999999px;
    padding: 0 35px;
  }

  .clarity-scale.pb-0 {
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
    color: #333;
    font-size: 16px;
    line-height: 1.56;
    box-sizing: border-box;
    max-height: 999999px;
    padding-bottom: 0 !important;
    padding: 5% 0;
  }

  .diamond-lightbox-hand {
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
    color: #333;
    font-size: 16px;
    line-height: 1.56;
    box-sizing: border-box;
    max-height: 999999px;
    max-width: 280px;
    margin: auto;
  }

  .hand-wrap {
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
    color: #333;
    font-size: 16px;
    line-height: 1.56;
    box-sizing: border-box;
    max-height: 999999px;
    position: relative;
  }

  .image-responsive.center-block {
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
    color: #333;
    font-size: 16px;
    line-height: 1.56;
    box-sizing: border-box;
    border: 0;
    vertical-align: middle;
    max-height: 999999px;
    height: auto;
    max-width: 100%;
    display: block;
    margin-left: auto;
    margin-right: auto;
  }

  .carat-image-assets {
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
    color: #333;
    font-size: 16px;
    line-height: 1.56;
    box-sizing: border-box;
    max-height: 999999px;
    background-position: center center;
    background-repeat: no-repeat;
    cursor: default;
    height: 40%;
    left: 50%;
    margin-left: -25%;
    margin-top: -18%;
    position: absolute;
    top: 50%;
    width: 40%;
    z-index: 2;
    background-size: 22%;
  }

  .definition-of-carat-tips {
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    font-family: 'Avenir Book', Arial, Helvetica, sans-serif;
    color: #333;
    line-height: 1.56;
    box-sizing: border-box;
    margin: 0 0 10px;
    max-height: 999999px;
    text-align: center;
    font-size: 13px;
    margin-top: 0.5em;
    margin-bottom: 0 !important;
  }

  .size-input-slider {
    max-width: 470px;
    display: flex;
    justify-content: space-between;
    flex-direction: row;
    margin-left: auto;
    margin-right: auto;
  }

  .size-input-slider span {
    font-size: 12px;
    margin: 0 4px;
  }

  .custom-slider-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 16px;
    width: 75%;
    position: relative;
    margin-bottom: 30px;

    .custom-slider-info {
      position: absolute;
      top: 24px;
    }
  }

  .custom-slider {
    -webkit-appearance: none;
    appearance: none;
    background: transparent;
    cursor: pointer;
    width: 280px;
  }

  .custom-slider::-webkit-slider-runnable-track {
    background: #183e40;
    height: 6px;
  }

  .custom-slider::-moz-range-track {
    background: #183e40;
    height: 6px;
  }

  .custom-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    margin-top: -7px;
    border-radius: 50%;
    height: 20px;
    width: 20px;
    background: #183e40;
  }

  .custom-slider::-moz-range-thumb {
    border: none;
    border-radius: 0;
    border-radius: 50%;
    height: 20px;
    width: 20px;
    background: #183e40;
  }

  @media screen and (max-width: 640px) {
    padding: 0;

    .search-noUiSlider-wrap {
      padding: 0;
    }

    .size-input-slider {
      margin: 0 auto;
      padding: 0;
      width: 320px;

      .custom-slider-container {
        width: 210px;
      }
    }
  }

  @media screen and (max-width: 375px) {
    .size-input-slider {
      width: 280px;
      .custom-slider-container {
        width: 160px;
      }
    }
  }
`;

export default function CaratSliderHandGraphic({
  content,
  actions,
  contentPosition,
  mediaAlignment,
  anchorLinkID,
  optionalLargeHeader,
  margin = false,
}: ICaratSliderHandGraphicProps) {
  const isAboveXSScreen = useMediaQuery('(min-width: 375px)');
  const isAboveSMScreen = useMediaQuery('(min-width: 640px)');
  const [caratValue, setCaratValue] = useState<number>(175);
  const deferredSize = useDeferredValue(caratValue);
  const formattedActions = actions?.map(action => ({
    ...action,
    // @ts-ignore
    color: action.color === 'dark' ? 'dark-green' : 'white',
  }));

  let infoPosition;

  if (isAboveSMScreen) {
    infoPosition = caratValue * 1.5 + 5;
  } else if (isAboveXSScreen) {
    infoPosition = caratValue * 1.1 - 7;
  } else {
    infoPosition = caratValue * 0.8 - 5;
  }

  return (
    <div
      {...getAnchorId(anchorLinkID)}
      className={classNames('tw-w-[85%] tablet:tw-w-[85%] tw-max-w-[1440px] tw-mx-auto', getComponentMargin(margin))}
    >
      {optionalLargeHeader?.content && (
        <Title
          component={optionalLargeHeader?.titleHTMLTag || 'h2'}
          weight={optionalLargeHeader?.titleWeight}
          size={optionalLargeHeader?.titleSize}
          alignment="center"
        >
          {optionalLargeHeader?.content}
        </Title>
      )}

      <div
        className={classNames(
          'tw-flex tw-items-center lg:tw-mx-auto tw-flex-col lg:tw-flex-row tw-max-w-[1172px] tw-w-full lg:tw-w-full tw-gap-x-[64px] lg:tw-max-w-[1440px]',
          {
            'lg:tw-flex-row-reverse': contentPosition === 'right',
            'lg:tw-items-start ': mediaAlignment === 'start',
          }
        )}
      >
        <div className="tw-w-full">
          <Container>
            <div className="col-lg-6 col-md-6 detail" id="id-carat-weight">
              <div className="budget-box">
                <div className="carat-weight-responsive">
                  <div className="search-noUiSlider-wrap">
                    <div className="clarity-scale pb0">
                      <div className="diamond-lightbox-hand">
                        <div className="hand-wrap">
                          <div className="tw-relative tw-aspect-square">
                            <Image
                              src="https://image.brilliantearth.com/static/img/handskin/loose-diamond-hand_dark_updated.jpg"
                              fill
                              className="img-responsive center-block"
                              alt="Hand"
                            />
                          </div>
                          <span
                            style={{
                              backgroundImage:
                                'url("//css.brilliantearth.com/static/imgProd/diamond-size-visualization/image-assets/Round_Zoom.png")',
                              backgroundSize: `${22 + deferredSize * 0.125}%`,
                            }}
                            className="carat-image-assets"
                            id="carat_lightbox_diamond_image"
                          />
                        </div>
                        <p className="definition-of-carat-tips">Actual Diamond Size on Size 6 Hand</p>
                      </div>
                    </div>
                  </div>
                  <div className="size-input-slider">
                    <span>0.25 CT</span>
                    <div className="custom-slider-container">
                      <input
                        type="range"
                        onChange={e => setCaratValue(+e.target.value)}
                        min="0"
                        max="175"
                        value={caratValue}
                        step="1"
                        className="custom-slider"
                        aria-label={`Selected carat: ${caratValue}`}
                      />
                      <span className="custom-slider-info" style={{ left: `${infoPosition}px` }}>
                        {(caratValue * 0.01 + 0.25).toFixed(2)}
                      </span>
                    </div>
                    <span>2.00 CT</span>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </div>

        <div className="tw-w-full lg:tw-max-w-[1172px] tw-mt-be-6 lg:tw-mt-0">
          <RichText content={content} />

          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 md:tw-flex-row tw-gap-2 md:tw-gap-4 tw-w-full tw-mt-12">
            {formattedActions?.slice(0, 4).map((action, i) => {
              const { label, color, type } = action;

              if (action.actions) {
                const { actions: subActions } = action;
                return (
                  <DropdownButton
                    key={i}
                    label={label}
                    options={subActions}
                    // @ts-ignore
                    color={color}
                    type={type}
                    className={beTwMerge(
                      `tw-basis-[calc(50%-8px)] tw-w-full tw-text-center tw-max-w-none [&>button]:tw-w-full [&>button]:tw-h-full [&>button]:tw-leading-4 [&>button]:tw-py-3 [&>button>span]:tw-max-w-[calc(100%-28px)] [&>button>span]:tw-overflow-hidden tw-h-[44px] tw-whitespace-nowrap [&>button>span]:tw-text-ellipsis`
                    )}
                  />
                );
              }
              const { onClick, href } = action;
              return (
                <Button
                  className={beTwMerge(
                    `tw-basis-[calc(50%-8px)] tw-max-w-none tw-w-full tw-text-center tw-whitespace-nowrap tw-text-ellipsis md:tw-overflow-hidden`
                  )}
                  key={i}
                  ariaLabel={moreDescriptiveAriaLabel(optionalLargeHeader?.content, label)}
                  label={label}
                  // @ts-ignore
                  color={color}
                  type={type}
                  href={href}
                  onClick={onClick}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
